import {
  SNAKE_MAX_SCORES,
  SNAKE_SPEED_ORDER,
  snakeBestsSchema,
  type SnakeBestResponse,
  type SnakeBests,
  type SnakeSpeed,
} from "@bing/shared";

/**
 * «مار» — THE BEST SCORES: one per player AT EACH SPEED (the operator, 2026-11-02), kept by the
 * server so they survive a new phone, and mirrored on the device so the start screen can show the
 * chosen speed's at once.
 *
 *  - SHOWN: at each speed, the device's best and the server's, whichever is higher.
 *  - SAVED: a score goes to the server WITH ITS SPEED when a run ends, when the player leaves a
 *    running game through «خروج», and on a pause when it beats that speed's best known — always with
 *    `keepalive` (the api client's `postSnakeScore`), so Telegram killing the backgrounded app loses
 *    nothing. Never a 0, and never a score the server is already known to beat at that speed.
 *  - A POST THAT FAILS leaves the score PENDING on the device — one pending score per speed — and the
 *    next start screen retries each ONCE. It is written BEFORE the request leaves, so an app killed
 *    mid-flight retries it too (a duplicate costs nothing: the server keeps the max).
 *
 * Every device value is keyed by the player and the speed, so a second Telegram account on the same
 * phone never sees — or posts — another player's record, and no speed ever reads another's. (The
 * first build's one best and one pending score, under the player's key alone, are never read again:
 * which speed they were set at was never recorded.)
 */

/** The slice of `localStorage` this needs (the tests pass a map). */
export interface SnakeBestStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The slice of the api client this needs. */
export interface SnakeBestApi {
  getSnakeBest(): Promise<SnakeBestResponse>;
  postSnakeScore(speed: SnakeSpeed, score: number): Promise<SnakeBestResponse>;
}

/** What a read learns: the bests to show, the server's own, and the refusal if it refused. */
export interface SnakeBestRefresh {
  /** The best to show at each speed: max(device, server). */
  best: SnakeBests;
  /** The server's bests as this session has heard them, or null when this read heard nothing (a
   *  failure). */
  server: SnakeBests | null;
  error: unknown;
}

const BEST_KEY = "bing:snakeBest:";
const PENDING_KEY = "bing:snakePending:";

/** A stored count, or 0 for anything else — never above that speed's bound (the api refuses more). */
function readCount(storage: SnakeBestStorage, key: string, speed: SnakeSpeed): number {
  try {
    const n = Number(storage.getItem(key));
    return Number.isInteger(n) && n > 0 ? Math.min(n, SNAKE_MAX_SCORES[speed]) : 0;
  } catch {
    return 0;
  }
}

export class SnakeBestScore {
  /** The server's answers this session, or null before it has said anything. */
  private server: SnakeBests | null = null;

  constructor(
    private readonly storage: SnakeBestStorage,
    private readonly api: SnakeBestApi,
    /** The player — `profile.id`. */
    private readonly owner: string,
  ) {}

  /** The best this device has seen at `speed`: its own runs and the server's answers. */
  device(speed: SnakeSpeed): number {
    return readCount(this.storage, this.key(BEST_KEY, speed), speed);
  }

  /** A score at `speed` the server has not confirmed yet, or 0. */
  pending(speed: SnakeSpeed): number {
    return readCount(this.storage, this.key(PENDING_KEY, speed), speed);
  }

  /** The best to show at `speed`: the device's and the server's, whichever is higher. */
  best(speed: SnakeSpeed): number {
    return Math.max(this.device(speed), this.server?.[speed] ?? 0);
  }

  /** The best to show at every speed. */
  get bests(): SnakeBests {
    return { slow: this.best("slow"), normal: this.best("normal"), fast: this.best("fast") };
  }

  /**
   * The start screen: every pending score is retried — once, each with its speed, and each answer
   * also carries the server's bests — or else the server's bests are read. Never rejects: a failure
   * keeps the cached bests on screen, and comes back as `error` for the caller to read (a refusal by
   * the platform's gates).
   */
  async refresh(): Promise<SnakeBestRefresh> {
    const retries = SNAKE_SPEED_ORDER.flatMap((speed) => {
      const score = this.pending(speed);
      return score > 0 ? [() => this.api.postSnakeScore(speed, score)] : [];
    });
    return this.settle(retries.length > 0 ? retries : [() => this.api.getSnakeBest()]);
  }

  /**
   * A finished game: ONE read of the server's bests — never a retry, the run's own score is already
   * on its way — whose answer is also the gates' verdict on the next game («دوباره»).
   */
  async check(): Promise<SnakeBestRefresh> {
    return this.settle([() => this.api.getSnakeBest()]);
  }

  /** A score to keep at `speed`: a run's end, a «خروج» from a running game, a pause that beats the
   *  best. */
  save(speed: SnakeSpeed, score: number): void {
    const value = Math.min(SNAKE_MAX_SCORES[speed], Math.floor(score));
    if (!(value > 0)) return; // never a 0
    if (value > this.device(speed)) this.write(BEST_KEY, speed, value);
    const send = Math.max(value, this.pending(speed));
    const known = this.server?.[speed];
    if (known !== undefined && send <= known) {
      this.clearPendingUpTo(speed, known);
      return; // the server already holds as much at this speed
    }
    this.write(PENDING_KEY, speed, send); // before the request: a killed app retries it next time
    this.api.postSnakeScore(speed, send).then(
      ({ best }) => this.hear(best),
      () => {
        // stays pending; the next start screen retries it once
      },
    );
  }

  /** The requests, side by side — each answer heard as it is, the first failure handed back. */
  private async settle(calls: (() => Promise<SnakeBestResponse>)[]): Promise<SnakeBestRefresh> {
    const outcomes = await Promise.all(
      calls.map(async (call): Promise<{ heard: boolean } | { error: unknown }> => {
        try {
          return { heard: this.hear((await call()).best) };
        } catch (error) {
          return { error };
        }
      }),
    );
    const failed = outcomes.find((outcome) => "error" in outcome);
    return {
      best: this.bests,
      server: outcomes.some((outcome) => "heard" in outcome && outcome.heard) ? this.server : null,
      error: failed && "error" in failed ? failed.error : null,
    };
  }

  /** The server said its bests are `bests` — false, and nothing heard, when the answer is not the
   *  three of them (an api older than the bests per speed). */
  private hear(bests: unknown): boolean {
    const parsed = snakeBestsSchema.safeParse(bests);
    if (!parsed.success) return false;
    const known = this.server;
    const server = { ...parsed.data };
    for (const speed of SNAKE_SPEED_ORDER) {
      const best = parsed.data[speed];
      server[speed] = Math.max(known?.[speed] ?? 0, best);
      if (best > this.device(speed)) this.write(BEST_KEY, speed, best);
      this.clearPendingUpTo(speed, best);
    }
    this.server = server;
    return true;
  }

  /** A pending score the server now holds is done; a higher one (a later run's) stays. */
  private clearPendingUpTo(speed: SnakeSpeed, best: number): void {
    const pending = this.pending(speed);
    if (pending > 0 && pending <= best) {
      try {
        this.storage.removeItem(this.key(PENDING_KEY, speed));
      } catch {
        // storage gone (private mode): nothing to clear
      }
    }
  }

  private write(prefix: string, speed: SnakeSpeed, value: number): void {
    try {
      this.storage.setItem(this.key(prefix, speed), String(value));
    } catch {
      // storage refused (private mode, full): the server still has it, or will
    }
  }

  private key(prefix: string, speed: SnakeSpeed): string {
    return `${prefix}${this.owner}:${speed}`;
  }
}
